import React from 'react';

/**
 * Animated gesture illustrations for the help overlay.
 * Each gesture shows a miniature UI context so the user
 * recognises what they're interacting with.
 */

/** Tap the card body — front card slides away, the next one rises behind it.
    Distinct from TapGesture below, which targets the CTA button. */
export const TapCardGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--tapcard">
    {/* Next card, waiting behind */}
    <g className="ho-tapcard-next">
      <rect x="42" y="16" width="76" height="72" rx="10" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
      <rect x="52" y="30" width="30" height="4" rx="2" fill="rgba(255,255,255,0.18)" />
      <rect x="52" y="38" width="46" height="3" rx="1.5" fill="rgba(255,255,255,0.1)" />
    </g>

    {/* Front card — the one being tapped away */}
    <g className="ho-tapcard-front">
      <rect x="38" y="10" width="84" height="80" rx="10" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
      <rect x="38" y="10" width="84" height="34" rx="10" fill="rgba(255,255,255,0.06)" />
      <rect x="38" y="34" width="84" height="10" fill="rgba(255,255,255,0.06)" />
      <rect x="48" y="52" width="34" height="4" rx="2" fill="rgba(255,255,255,0.3)" />
      <rect x="48" y="60" width="52" height="3" rx="1.5" fill="rgba(255,255,255,0.15)" />
      <rect x="48" y="70" width="64" height="10" rx="5" fill="rgba(212,113,90,0.5)" />
    </g>

    {/* Finger on the card body, above the CTA — a card tap, not a button tap */}
    <g className="ho-tapcard-finger">
      <circle cx="80" cy="40" r="13" fill="white" opacity="0.9" />
      <circle cx="80" cy="40" r="5.5" fill="rgba(26,24,20,0.3)" />
    </g>
  </svg>
);

/** Pinch — two fingers on a card that shrinks into grid squares */
export const PinchGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--pinch">
    {/* Card outline (what you're pinching) */}
    <rect x="40" y="12" width="80" height="76" rx="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeDasharray="4 3" />

    {/* Mini grid squares (what it becomes) */}
    <rect x="46" y="18" width="32" height="28" rx="4" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    <rect x="82" y="18" width="32" height="28" rx="4" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    <rect x="46" y="52" width="32" height="28" rx="4" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    <rect x="82" y="52" width="32" height="28" rx="4" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

    {/* Tiny content inside grid squares */}
    <rect x="50" y="23" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.2)" />
    <rect x="86" y="23" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.2)" />
    <rect x="50" y="57" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.2)" />
    <rect x="86" y="57" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.2)" />

    {/* Spread arrows */}
    <polyline points="36,50 28,50" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" />
    <polyline points="124,50 132,50" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" />

    {/* Left finger */}
    <g className="ho-pinch-left">
      <circle cx="56" cy="50" r="12" fill="white" opacity="0.9" />
      <circle cx="56" cy="50" r="5" fill="rgba(26,24,20,0.3)" />
    </g>
    {/* Right finger */}
    <g className="ho-pinch-right">
      <circle cx="104" cy="50" r="12" fill="white" opacity="0.9" />
      <circle cx="104" cy="50" r="5" fill="rgba(26,24,20,0.3)" />
    </g>
  </svg>
);

/** Tap — finger pressing a CTA button on a card */
export const TapGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--tap">
    {/* Task card */}
    <rect x="35" y="8" width="90" height="84" rx="10" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
    {/* Card top area */}
    <rect x="35" y="8" width="90" height="36" rx="10" fill="rgba(255,255,255,0.06)" />
    <rect x="35" y="34" width="90" height="10" rx="0" fill="rgba(255,255,255,0.06)" />
    {/* Card content */}
    <rect x="46" y="52" width="36" height="4" rx="2" fill="rgba(255,255,255,0.3)" />
    <rect x="46" y="60" width="56" height="3" rx="1.5" fill="rgba(255,255,255,0.15)" />
    {/* CTA button — the target */}
    <rect x="46" y="70" width="68" height="14" rx="7" fill="rgba(212,113,90,0.6)" />
    <rect x="62" y="75" width="36" height="3" rx="1.5" fill="rgba(255,255,255,0.5)" />

    {/* Ripple rings on the button */}
    <circle cx="80" cy="77" r="16" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none" className="ho-tap-ring ho-tap-ring--1" />
    <circle cx="80" cy="77" r="24" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="none" className="ho-tap-ring ho-tap-ring--2" />

    {/* Finger tapping the button */}
    <g className="ho-tap-finger">
      <circle cx="80" cy="77" r="12" fill="white" opacity="0.9" />
      <circle cx="80" cy="77" r="5" fill="rgba(26,24,20,0.3)" />
    </g>
  </svg>
);

/** Task bar tap — a bar with dots + tap gesture (already contextual) */
export const TaskBarGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--taskbar">
    {/* Faded card above the bar for spatial context */}
    <rect x="35" y="4" width="90" height="48" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <rect x="46" y="18" width="36" height="4" rx="2" fill="rgba(255,255,255,0.1)" />
    <rect x="46" y="26" width="50" height="3" rx="1.5" fill="rgba(255,255,255,0.06)" />

    {/* Bar shape */}
    <rect x="20" y="60" width="120" height="28" rx="14" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    {/* Dot indicators inside bar */}
    <circle cx="40" cy="74" r="3.5" fill="rgba(110,122,90,0.8)" />
    <circle cx="52" cy="74" r="3.5" fill="rgba(110,122,90,0.8)" />
    <circle cx="64" cy="74" r="3.5" fill="rgba(212,113,90,0.8)" />
    <circle cx="76" cy="74" r="3.5" fill="rgba(232,228,223,0.4)" />
    <circle cx="88" cy="74" r="3.5" fill="rgba(232,228,223,0.4)" />
    {/* Count text */}
    <text x="100" y="77" fill="rgba(255,255,255,0.35)" fontSize="8" fontWeight="600">2 left</text>

    {/* Tap finger */}
    <g className="ho-tap-finger">
      <circle cx="80" cy="74" r="12" fill="white" opacity="0.9" />
      <circle cx="80" cy="74" r="5" fill="rgba(26,24,20,0.3)" />
    </g>
    {/* Ripple */}
    <circle cx="80" cy="74" r="20" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none" className="ho-tap-ring ho-tap-ring--1" />
  </svg>
);

/** Pulse — row of progress dots with active one pulsing */
export const PulseGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--pulse">
    {/* Faded card below for spatial context */}
    <rect x="35" y="58" width="90" height="36" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

    {/* Progress dot row */}
    <circle cx="44" cy="40" r="4" fill="rgba(110,122,90,0.8)" />
    <circle cx="58" cy="40" r="4" fill="rgba(110,122,90,0.8)" />
    {/* Active dot — pill shape */}
    <rect x="68" y="36" width="20" height="8" rx="4" fill="rgba(212,113,90,0.9)" />
    <circle cx="96" cy="40" r="4" fill="rgba(232,228,223,0.35)" />
    <circle cx="110" cy="40" r="4" fill="rgba(232,228,223,0.35)" />

    {/* Pulse rings around the active dot */}
    <ellipse cx="78" cy="40" rx="20" ry="16" fill="rgba(255,255,255,0.06)" className="ho-pulse-outer" />
    <ellipse cx="78" cy="40" rx="14" ry="11" fill="rgba(255,255,255,0.1)" className="ho-pulse-mid" />

    {/* Label */}
    <text x="52" y="24" fill="rgba(255,255,255,0.3)" fontSize="7" fontWeight="600" textAnchor="middle">done</text>
    <text x="78" y="24" fill="rgba(255,255,255,0.5)" fontSize="7" fontWeight="700" textAnchor="middle">now</text>
    <text x="103" y="24" fill="rgba(255,255,255,0.2)" fontSize="7" fontWeight="500" textAnchor="middle">todo</text>
  </svg>
);

/** Slide up — finger pulling up the submissions bar, expanded content peeking */
export const SlideUpGesture: React.FC = () => (
  <svg viewBox="0 0 160 100" fill="none" className="ho-gesture ho-gesture--slideup">
    {/* Expanded content peeking above (what appears when you slide up) */}
    <rect x="24" y="4" width="112" height="20" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    <circle cx="38" cy="14" r="4" fill="rgba(110,122,90,0.4)" />
    <rect x="48" y="11" width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.12)" />
    <text x="120" y="17" fill="rgba(110,122,90,0.4)" fontSize="7" fontWeight="600">Done</text>

    <rect x="24" y="28" width="112" height="20" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    <circle cx="38" cy="38" r="4" fill="rgba(110,122,90,0.4)" />
    <rect x="48" y="35" width="34" height="3" rx="1.5" fill="rgba(255,255,255,0.12)" />
    <text x="120" y="41" fill="rgba(110,122,90,0.4)" fontSize="7" fontWeight="600">Done</text>

    {/* Bottom bar (the thing you pull up) */}
    <rect x="20" y="58" width="120" height="28" rx="14" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    {/* Check icon */}
    <polyline points="34,72 38,76 44,68" stroke="rgba(110,122,90,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    {/* Bar text */}
    <text x="52" y="75" fill="rgba(255,255,255,0.4)" fontSize="8" fontWeight="600">3 submitted</text>
    {/* Chevron */}
    <polyline points="122,76 126,70 130,76" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" fill="none" />

    {/* Upward arrow hint */}
    <line x1="80" y1="92" x2="80" y2="56" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeDasharray="3 3" />
    <polyline points="74,62 80,56 86,62" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" fill="none" strokeLinecap="round" />

    {/* Finger */}
    <g className="ho-slideup-hand">
      <circle cx="80" cy="72" r="12" fill="white" opacity="0.9" />
      <circle cx="80" cy="72" r="5" fill="rgba(26,24,20,0.3)" />
    </g>
  </svg>
);
